import React, { useRef, useEffect, useState, useContext } from 'react';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    select as D3select,
    max as D3max,
} from 'd3';
import { debounce } from 'lodash';
import Moment from 'moment';

import LinearProgress from '@mui/material/LinearProgress';

import D3TimelineSlider from '../../d3/D3TimelineSlider';
import DataContext from '../../data/data-context';

const useStyles = makeStyles(theme => ({
    root: {
        fill: theme.ChartColors[0],

        '&.bt': {
            fill: theme.palette.backtesting.main,
        },

        '& .tick--minor': {
            opacity: 0.2,
        },

        '& .axis--grid .domain, .axis--x .domain': {
            fill: 'none',
            stroke: 'none',
        },

        '& .axis--x line': {
            stroke: 'none',
        },

        '& .brush .selection': {
            fillOpacity: 0.65,
            fill: theme.styled.slider.window,
        },

        '& .brush .overlay': {
            fill: theme.styled.slider.overlay, //theme.palette.grey[600],
            fillOpacity: 0.3,
        },

        '& .brush.invisibles .overlay': {
            fill: 'none',
        },

        '& .brush.invisibles .selection': {
            fill: theme.styled.slider.window, // theme.palette.grey[600],
        },

        '& .brush.invisibles .handle': {
            fill: 'none',
            stroke: 'none',
        },

        '& .handle': {
            fill: theme.palette.grey[300],
            fillOpacity: 0.55,
            stroke: theme.palette.grey[300],
        }
    },
    progress: {
        width: '100%',
        paddingLeft: 7.5,
        paddingRight: 20,
        // reverse progressbar
        '& .MuiLinearProgress-colorPrimary': {
            backgroundColor: theme.palette.primary.light,
            '& .MuiLinearProgress-barColorPrimary': {
                backgroundColor: theme.palette.primary.dark,
            }
        },

        '&.bt': {
            '& .MuiLinearProgress-colorPrimary': {
                backgroundColor: theme.palette.backtesting.main,
                '& .MuiLinearProgress-barColorPrimary': {
                    backgroundColor: theme.palette.backtesting.table,
                }
            },
        }
    },
}));

const sidenavw = 250;
const collapsed = 30;
const donut = 260;

const TimelineChartWithSlider = ({
    selection = [],
    progress = 100,
    ...props
}) => {
    const d3Container = useRef(null);
    const classes = useStyles();
    // const theme = useTheme();

    const { sidenav } = useContext(DataContext);
    const [delaySidenav, setDetaySidenav] = useState(!sidenav.collapsed);
    const [width, setWidth] = useState(window.innerWidth - donut);
    const [, setCancelTimeout] = useState();

    // resize hook
    useEffect(() => {
        const resize = debounce(() => { setWidth(window.innerWidth - donut) }, 111);
        window.addEventListener('resize', resize);
        return () => {
            window.removeEventListener('resize', resize);
        };
    }, []);

    // sidenav open hook
    useEffect(() => {
        /// need to delay sidebar open to avoid wrapping layout
        setCancelTimeout(current => {
            if (current) clearTimeout(current);
            if (!sidenav.collapsed) {
                setDetaySidenav(!sidenav.collapsed);
                return undefined;
            }
            return (setTimeout(() => setDetaySidenav(!sidenav.collapsed), 420));
        });
    }, [sidenav.collapsed]);

    useEffect(() => {
        if (props.dataservice && d3Container.current) {
            const node = d3Container.current;
            // default selection
            const send = selection && selection.length > 1 && selection[1]
                ? selection[1] : D3max(props.data, props.dataservice.timestamp);
            const sstart = selection && selection.length > 0
                ? selection[0] : Moment(send).subtract(6, 'h').toDate();

            props.data.sort((a, b) => props.dataservice.timestamp(a).getTime() -
                props.dataservice.timestamp(b).getTime());

            const g = D3select(node)
                .data([props.data])
                .join('g')
                .call(D3TimelineSlider()
                    .dataservice(props.dataservice)
                    .color(() => undefined)
                    .width(width - (delaySidenav ? sidenavw : collapsed))
                    .height(props.height)
                    .selection([Moment(sstart).toDate(), Moment(send).toDate()])
                    .start(props.start)
                    .end(props.end)
                    .on('changed', props.onSelection));
            g.exit().remove();
        }
    }, [
        width,
        delaySidenav,
        props.data,
        selection,
        props.start,
        props.end,
        props.height,
        props.onSelection,
        props.dataservice,
    ]);

    return (
        <div
            className={clsx(classes.root, {
                bt: props.isBT,
            })}
            ref={d3Container}>
            { progress < 100 &&
                <div className={clsx(classes.progress, { bt: props.isBT })}>
                    <LinearProgress
                        color="primary"
                        variant="determinate"
                        value={100 - progress} />
                </div>
            }
        </div>
    );
};

TimelineChartWithSlider.propTypes = {
    data: PropTypes.array.isRequired,
    start: PropTypes.string,
    end: PropTypes.string,
    height: PropTypes.number,
    dataservice: PropTypes.object,
    selection: PropTypes.array,
    onSelection: PropTypes.func,
    isBT: PropTypes.bool,
    progress: PropTypes.number,
};

// recall that timeline fills in chunks from end to start
const CalculateProgress = (start, end, current) => {
    const sd = new Date(start);
    const ed = end ? new Date(end) : new Date();
    const cd = new Date(current);
    if (isNaN(sd) || isNaN(ed) || isNaN(cd)) return 100;

    const s = Math.floor(sd.getTime() / 1000);
    const e = Math.floor(ed.getTime() / 1000);
    const c = Math.floor(cd.getTime() / 1000);

    if (s >= e) return 100;
    if (c <= s || c > e) return 100;
    const total = e - s;

    return 100 * (e - c) / total;
};

export {
    TimelineChartWithSlider as default,
    CalculateProgress,
};
